import { Route } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { OfferType } from 'constants/enums';
import AssetsView from 'views/Assets/AssetsView';
import BadgesView from 'views/Badges/BadgesView';
import CheckoutDesignView from 'views/CheckoutDesign/CheckoutDesignView';
import CheckoutLinksView from 'views/CheckoutLinks/CheckoutLinksView';
import LocalizationView from 'views/Localization/LocalizationView';
import OfferDesignView from 'views/OfferDesign/OfferDesignView';
import { OffersView } from 'views/Offers/OffersView';
import OffersUIView from 'views/OffersUI/OffersUIView';
import PlansView from 'views/Plans/PlansView';
import { PlayersView } from 'views/Players/PlayersView';
import { PricingView } from 'views/Pricing/PricingView';
import ProductsView from 'views/Products/ProductsView';
import ProfileView from 'views/Profile/ProfileView';
import ProgressBarsView from 'views/ProgressBars/ProgressBarsView';
import FinancialReportsTable from 'views/Reports/FinancialReports';
import PayoutsTable from 'views/Reports/Payouts';
import ReportsView from 'views/Reports/ReportsView';

import { Order } from 'components/Order/Order';
import { Orders } from 'components/Orders/Orders/Orders';
import PasswordTab from 'components/PasswordsTab/PasswordTab';
import PersonalInfo from 'components/PersonalInfo/PersonalInfo';
import { BlockedPlayersTable } from 'components/Players/BlockedPlayersTable/BlockedPlayersTable';
import PricingForm from 'components/Pricing/PricingForm';
import PricingSettings from 'components/Pricing/PricingSettings';
import PricingTable from 'components/Pricing/PricingTable';
import { TemplatesStudioView } from 'components/TemplatesStudio/Components/TemplatesStudio';
import { AccountApprovalModalTheme } from 'components/Theme/AccountApprovalModalTheme';
import CheckoutTheme from 'components/Theme/CheckoutTheme/CheckoutTheme';
import { CompletedTheme } from 'components/Theme/CompletedTheme/CompletedTheme';
import { GeneralSettings } from 'components/Theme/GeneralSettings/GeneralSettings';
import { LoginTheme } from 'components/Theme/LoginTheme/LoginTheme';
import ReceiptTheme from 'components/Theme/ReceiptTheme/ReceiptTheme';
import { StoreSettingsView } from 'components/Theme/StoreSettings/StoreSettingsView';
import { StoreTheme } from 'components/Theme/StoreTheme/StoreTheme';
import { ThemeView } from 'components/Theme/Theme';

import SubscriptionsView from '../../views/Subscriptions/SubscriptionsView';
import { ERouteContext, ESharedRoutes } from '../types';

import { getSharedSettingsRoutes } from './settingsRoutesUtil';

export const getSharedRoutes = (
  name: ESharedRoutes,
  context = ERouteContext.PROJECT
): JSX.Element => {
  const sharedRoutesMap = {
    [ESharedRoutes.PLAYERS]: (
      <Route path="players/*" element={<PlayersView />}>
        <Route path="blocked" element={<BlockedPlayersTable />} />
      </Route>
    ),
    [ESharedRoutes.LOCALIZATION]: (
      <Route path="localization/*" element={<LocalizationView />} />
    ),
    [ESharedRoutes.SETTINGS]: getSharedSettingsRoutes(context),
    [ESharedRoutes.PROFILE]: (
      <Route path="profile" element={<ProfileView />}>
        <Route path="personal" element={<PersonalInfo />} />
        <Route path="password" element={<PasswordTab />} />
      </Route>
    ),
    [ESharedRoutes.THEME]: (
      <Route path="theme" element={<ThemeView context={context} />}>
        <Route path="store-settings" element={<StoreSettingsView />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route path="store" element={<StoreTheme />} />
        <Route path="login" element={<LoginTheme />} />
        <Route
          path="account-approval-modal"
          element={<AccountApprovalModalTheme />}
        />
        <Route path="completed" element={<CompletedTheme />} />
        <Route path="checkout" element={<CheckoutTheme />} />
        <Route path="receipt" element={<ReceiptTheme />} />
      </Route>
    ),
    [ESharedRoutes.TEMPLATES_STUDIO]: (
      <Route path="templates-studio/*" element={<TemplatesStudioView />} />
    ),
    [ESharedRoutes.PRODUCTS]: (
      <Route path="products/*" element={<ProductsView />} />
    ),
    [ESharedRoutes.OFFERS_UI]: (
      <Route path="offersui/*" element={<OffersUIView />} />
    ),
    [ESharedRoutes.OFFER_DESIGN]: (
      <Route path="offerdesign/*" element={<OfferDesignView />} />
    ),
    [ESharedRoutes.CHECKOUT_DESIGNS]: (
      <Route path="checkoutdesigns/*" element={<CheckoutDesignView />} />
    ),
    [ESharedRoutes.BADGES]: <Route path="badges/*" element={<BadgesView />} />,
    [ESharedRoutes.ASSETS]: <Route path="assets" element={<AssetsView />} />,
    [ESharedRoutes.BUNDLES]: (
      <Route path="bundles/*" element={<OffersView offerType={OfferType.BUNDLE} />} />
    ),
    [ESharedRoutes.PROMOTIONS]: (
      <Route path="promotions/*" element={<OffersView offerType={OfferType.SPECIAL_OFFER} />} />
    ),
    [ESharedRoutes.POPUPS]: (
      <Route path="popups/*" element={<OffersView offerType={OfferType.POPUP} />} />
    ),
    [ESharedRoutes.ROLLING]: (
      <Route path="rolling/*" element={<OffersView offerType={OfferType.ROLLING_OFFER} />} />
    ),
    [ESharedRoutes.CHECKOUT_LINKS]: (
      <Route path="checkoutlinks/*" element={<CheckoutLinksView />} />
    ),
    [ESharedRoutes.PROGRESS_BARS]: (
      <Route path="progressbars/*" element={<ProgressBarsView />} />
    ),
    [ESharedRoutes.SUBSCRIPTIONS]: (
      <Route path="subscriptions/*" element={<SubscriptionsView />} />
    ),
    [ESharedRoutes.PLANS]: <Route path="plans/*" element={<PlansView />} />,
    [ESharedRoutes.PRICING]: (
      <>
        <Route path="pricing" element={<PricingView context={context} />}>
          <Route path="points" element={<PricingTable />} />
          <Route path="settings" element={<PricingSettings />} />
        </Route>
        <Route path="pricing/form" element={<PricingForm />} />
        <Route
          path="pricing/points/form/:pricePointId"
          element={<PricingForm />}
        />
      </>
    ),
    [ESharedRoutes.ORDERS]: (
      <>
        <Route path="orders" element={<Orders />} />
        <Route
          path="orders/:orderId"
          element={<Order />}
        />
      </>
    ),
    [ESharedRoutes.REPORTS]: (
      <Route path="reports" element={<ReportsView />}>
        <Route path="" element={<Navigate to="financialreports" replace />} />
        <Route path="financialreports" element={<FinancialReportsTable />} />
        <Route path="payouts" element={<PayoutsTable />} />
      </Route>
    )
  };

  return sharedRoutesMap[name];
};
